import {
  addDays,
  addMonths,
  daysBetween,
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  type IsoDate,
} from './dates.ts';

export type PeriodKind = 'WEEK' | 'MONTH' | 'YEAR' | 'CUSTOM';

/** Inclusive date range. */
export type Period = {
  readonly kind: PeriodKind;
  readonly start: IsoDate;
  readonly end: IsoDate;
};

export type BucketSize = 'DAY' | 'WEEK' | 'MONTH';

export function periodContaining(kind: Exclude<PeriodKind, 'CUSTOM'>, date: IsoDate): Period {
  switch (kind) {
    case 'WEEK': {
      const start = startOfWeek(date);
      return { kind, start, end: addDays(start, 6) };
    }
    case 'MONTH':
      return { kind, start: startOfMonth(date), end: endOfMonth(date) };
    case 'YEAR':
      return { kind, start: startOfYear(date), end: endOfYear(date) };
  }
}

/** The period right before (same kind and length) — for "vs previous" comparisons. */
export function previousPeriod(period: Period): Period {
  switch (period.kind) {
    case 'WEEK':
      return periodContaining('WEEK', addDays(period.start, -7));
    case 'MONTH':
      return periodContaining('MONTH', addMonths(period.start, -1));
    case 'YEAR':
      return periodContaining('YEAR', addMonths(period.start, -12));
    case 'CUSTOM': {
      const length = daysBetween(period.start, period.end);
      const end = addDays(period.start, -1);
      return { kind: 'CUSTOM', start: addDays(end, -length), end };
    }
  }
}

export function periodDays(period: Period): number {
  return daysBetween(period.start, period.end) + 1;
}

/** Natural chart granularity: days for week/month, months for a year, custom by length. */
export function bucketSizeFor(period: Period): BucketSize {
  if (period.kind === 'YEAR') return 'MONTH';
  if (period.kind === 'CUSTOM') {
    const days = periodDays(period);
    if (days > 186) return 'MONTH';
    if (days > 62) return 'WEEK';
  }
  return 'DAY';
}

export type Bucket = { readonly start: IsoDate; readonly end: IsoDate };

export function buckets(period: Period, size: BucketSize = bucketSizeFor(period)): Bucket[] {
  const result: Bucket[] = [];
  let cursor =
    size === 'DAY' ? period.start : size === 'WEEK' ? startOfWeek(period.start) : startOfMonth(period.start);
  while (cursor <= period.end) {
    const end = size === 'DAY' ? cursor : size === 'WEEK' ? addDays(cursor, 6) : endOfMonth(cursor);
    result.push({ start: cursor < period.start ? period.start : cursor, end: end > period.end ? period.end : end });
    cursor = size === 'DAY' ? addDays(cursor, 1) : size === 'WEEK' ? addDays(cursor, 7) : addMonths(cursor, 1);
  }
  return result;
}

export function bucketIndex(bucketList: readonly Bucket[], date: IsoDate): number {
  // Buckets are sorted and contiguous; binary search keeps large year views cheap.
  let low = 0;
  let high = bucketList.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const bucket = bucketList[mid]!;
    if (date < bucket.start) high = mid - 1;
    else if (date > bucket.end) low = mid + 1;
    else return mid;
  }
  return -1;
}
